import { Injectable } from '@angular/core';
import { Auth, IdTokenResult, User, UserCredential } from 'firebase/auth';
import { Observable, filter, from, map, of, switchMap, take, throwError } from 'rxjs';
import { OpenAuthClient, injectFirebaseAuth, injectOpenAuth, signIn$ } from '@open/ngx-firebase/auth';
import { BusinessDb, injectDb } from './db';
import { BusinessApi, injectApi } from './api';
import { ConfirmationResponse } from '../../assets/responses/Confirmation.response';
import { LoginForUserWithPinRequest } from '../../assets/requests/auth/LoginForUserWithPin.request';
import { CheckUserPinValidityResponse } from '../../assets/responses/auth/CheckUserPinValidity.response';
import { UserModel } from '../../assets/models/user/User.model';
import { hasWebBusinessAccess } from '../utils/businessRoles.util';

export const USER_NOT_ALLOWED_ON_APP_ERROR: string = 'USER_NOT_ALLOWED_ON_APP';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  readonly #client: OpenAuthClient<UserModel> = injectOpenAuth<UserModel>({
    appKey: 'webBusiness',
    markActivatedOnPasswordUpdate: true,
    markActivatedOnPinLogin: false,
  });
  readonly #auth: Auth = injectFirebaseAuth();
  readonly #db: BusinessDb = injectDb();
  readonly #api: BusinessApi = injectApi();

  readonly user$: Observable<User | null> = this.#client.user$;
  readonly isAuthorized$: Observable<boolean> = this.user$.pipe(switchMap((user: User | null): Observable<boolean> => {
    if (!user) {
      return of(false);
    }
    return from(user.getIdTokenResult()).pipe(
      map(({ claims }: Pick<IdTokenResult, 'claims'>) => hasWebBusinessAccess(claims)),
    );
  }));

  logout(): Promise<void> {
    return this.#client.logout();
  }

  logout$(): Observable<void> {
    return this.#client.logout$();
  }

  login$(email: string, password: string): Observable<UserModel> {
    return signIn$(this.#auth, email, password).pipe(
      take(1),
      switchMap((credentials: UserCredential) => from(credentials.user.getIdTokenResult()).pipe(
        switchMap(({ claims }: Pick<IdTokenResult, 'claims'>) => {
          if (!hasWebBusinessAccess(claims)) {
            return this.logout$().pipe(
              switchMap(() => throwError(() => new Error(USER_NOT_ALLOWED_ON_APP_ERROR))),
            );
          }
          return this.#db.getDoc$<UserModel>('USERS', credentials.user.uid).pipe(
            filter((user): user is UserModel => !!user),
          );
        }),
      )),
    );
  }

  loginForUserWithPin$(request: LoginForUserWithPinRequest): Observable<UserModel> {
    return this.#client.loginWithPin$(request);
  }

  checkUserPinValidity$(request: LoginForUserWithPinRequest): Observable<CheckUserPinValidityResponse> {
    return this.#api.call$('AUTH-checkUserPinValidity_onCall', request);
  }

  checkIsActivatedUser$(email: string): Observable<{ isActivated: boolean }> {
    return this.#client.checkIsActivatedUser$(email);
  }

  createPinForUser$(email: string): Observable<ConfirmationResponse> {
    return this.#client.createPin$(email);
  }

  updateUserPassword$(password: string): Observable<UserModel> {
    return this.#client.updatePassword$(password);
  }

  acceptedCgu$(): Observable<void> {
    return this.#client.acceptCgu$();
  }
}
